import { createContext, useContext, useEffect, useState, useCallback } from 'react'

// ─── App-level Google Sign-In（GIS）───
// org policy DRS 擋 IAP allAuthenticatedUsers → 改 app-level：前端拿 Google ID token，
// 後端 verify_oauth2_token。任何 Google 帳號可登入（creator）；admin 端點由後端 allowlist 判 403。
//
// 全域 fetch patch：對同源 /api/ 請求自動附 Authorization: Bearer <id_token>，
// 讓整包搬來的 Onboarding/Admin 頁 fetch 呼叫零改動。

const AuthContext = createContext(null)
export const useAuth = () => useContext(AuthContext)

const TOKEN_KEY = 'qb_id_token'

function parseJwt(token) {
  try {
    const base64 = token.split('.')[1].replace(/-/g, '+').replace(/_/g, '/')
    return JSON.parse(decodeURIComponent(atob(base64).split('').map(
      c => '%' + ('00' + c.charCodeAt(0).toString(16)).slice(-2)).join('')))
  } catch { return null }
}

function tokenUser(token) {
  const p = token && parseJwt(token)
  if (!p) return null
  if (p.exp && p.exp * 1000 < Date.now() + 60_000) return null // 過期（留 60s 緩衝）
  return { email: p.email, name: p.name, picture: p.picture, sub: p.sub }
}

// 只 patch 一次；對 /api/ 同源請求加 Bearer
let fetchPatched = false
function patchFetch() {
  if (fetchPatched) return
  fetchPatched = true
  const orig = window.fetch.bind(window)
  window.fetch = (input, init = {}) => {
    const url = typeof input === 'string' ? input : input?.url || ''
    if (url.startsWith('/api/') || /^\/[a-z0-9][a-z0-9-]*\/api\//.test(url)) {
      const token = sessionStorage.getItem(TOKEN_KEY)
      if (token) {
        const headers = new Headers(init.headers || (typeof input !== 'string' ? input.headers : undefined))
        if (!headers.has('Authorization')) headers.set('Authorization', `Bearer ${token}`)
        init = { ...init, headers }
      }
    }
    return orig(input, init)
  }
}

// 模組載入即 patch（不能等 AuthProvider 的 useEffect）：React effect 順序是
// **子先父後**，直接開 /admin/checkup 這類深連結時，頁面 effect 的 fetch 會早於
// AuthProvider effect 執行 → 沒帶 Bearer → 401。7/22 實踩（健檢/關鍵字兩頁 overview 全 401）。
patchFetch()

// gisHl：Google 登入腳本的語言。預設 zh_TW → 既有呼叫端（/admin、/warroom）零改動。
// 只有三語的 /start 會傳它。
export function AuthProvider({ children, gisHl = 'zh_TW' }) {
  const [user, setUser] = useState(() => tokenUser(sessionStorage.getItem(TOKEN_KEY)))
  const [clientId, setClientId] = useState(null)
  const [gisReady, setGisReady] = useState(false)

  useEffect(() => { patchFetch() }, [])

  // client_id 由後端提供（免 rebuild 換 key）
  useEffect(() => {
    fetch('/api/v1/auth/config').then(r => r.ok ? r.json() : null)
      .then(d => d?.client_id && setClientId(d.client_id)).catch(() => {})
  }, [])

  const handleCredential = useCallback((resp) => {
    const token = resp?.credential
    const u = tokenUser(token)
    if (u) { sessionStorage.setItem(TOKEN_KEY, token); setUser(u) }
  }, [])

  // 載 GIS script → initialize 完才標 ready（renderButton 必須在 initialize 之後，
  // 否則 GSI 警告 "Failed to render button before calling initialize()" — 7/12 實踩）
  //
  // 🔴 `?hl=` 決定**可見那顆按鈕上的字**。renderButton 的 locale 只走到 iframe
  //    （`gsi/button?…&hl=en`），而 GIS 實際顯示的是父頁面的一個 <span>，
  //    它的字串表在**腳本載入時**就定了。只改 renderButton = 只改到看不見的那一半。
  //    hl 取自載入當下的語言：讀者從 /quanbrand/ 的 en/ja CTA 帶 ?lang= 進來，
  //    那時語言已經決定好了（LanguageProvider 在 AuthProvider 外層）。
  //    ⚠️ 載入後才切語言的話，這顆按鈕會停在載入時的語言直到重新整理 ——
  //    腳本的字串表換不掉。已知殘留，不是漏掉。
  useEffect(() => {
    if (!clientId) return
    const init = () => {
      try {
        window.google.accounts.id.initialize({ client_id: clientId, callback: handleCredential })
        setGisReady(true)
      } catch { /* GIS 未就緒則等下次 */ }
    }
    if (window.google?.accounts?.id) { init(); return }
    const s = document.createElement('script')
    s.src = 'https://accounts.google.com/gsi/client?hl=' + encodeURIComponent(gisHl)
    s.async = true
    s.onload = init
    document.head.appendChild(s)
  }, [clientId, handleCredential, gisHl])

  // 🔒 mock 登入（外部協作者離線開發用）：只在 build 時設 VITE_MOCK=1 才存在。
  //    正式 build 沒有這個環境變數 → 這段是死碼 → 不會出現在 production bundle。
  //    假 token 是「JWT 形狀」的無簽章字串，只給前端 tokenUser 解析；後端永遠看不到。
  const mockSignIn = useCallback(() => {
    if (import.meta.env.VITE_MOCK !== '1') return
    const b64 = (o) => btoa(unescape(encodeURIComponent(JSON.stringify(o)))).replace(/=+$/, '')
    const token = `${b64({ alg: 'none' })}.${b64({ sub: 'mock-user', email: 'you@example.com', name: '測試使用者',
      exp: Math.floor(Date.now() / 1000) + 8 * 3600 })}.mock`
    sessionStorage.setItem(TOKEN_KEY, token)
    setUser(tokenUser(token))
  }, [])

  const signOut = useCallback(() => {
    sessionStorage.removeItem(TOKEN_KEY)
    setUser(null)
    try { window.google?.accounts?.id?.disableAutoSelect() } catch {}
  }, [])

  return (
    <AuthContext.Provider value={{ user, signOut, gisReady, clientId, mockSignIn }}>
      {children}
    </AuthContext.Provider>
  )
}

// Google 官方登入鈕（GIS renderButton）· export 供自助漏斗 /start 自訂 landing 用
export function GoogleButton({
  loadingLabel = '登入服務初始化中…', locale = 'zh_TW',
  switchLabel = '使用其他 Google 帳號登入',
  fallbackLabel = '登入按鈕沒有載入。請檢查瀏覽器的第三方登入設定（FedCM／第三方 Cookie），或改用另一個瀏覽器；也可以直接從下方連結選擇帳號。',
} = {}) {
  const { gisReady, clientId, mockSignIn } = useAuth()
  // A-4／A-5（QB-CLOSE01 任務二）：
  // ① 瀏覽器已登入個人 Google 帳號時，GIS 會把按鈕畫成「以 xxx 身分繼續」，
  //    使用者沒有任何路徑改用公司帳號 —— 給一條永遠在的出口：Google 帳號選擇器，
  //    選完會回到這一頁，GIS 按鈕跟著換成選到的帳號。
  // ② GIS 的 iframe 被第三方登入設定擋掉時，renderButton 什麼都不畫、什麼都不報
  //    —— 「沒有錯誤訊息」與「沒有錯誤」在畫面上同形。initialize 之後 4 秒內
  //    容器裡還是空的，就把那句話印出來。
  const [rendered, setRendered] = useState(null)   // null=還在等 · true=畫出來了 · false=沒畫
  const chooserHref = 'https://accounts.google.com/AccountChooser?continue='
    + encodeURIComponent(typeof window !== 'undefined' ? window.location.href : '/')
  const ref = useCallback(node => {
    if (node && gisReady && clientId) {
      // 🔴 locale 決定的是**按鈕上那行字**（「使用 Google 帳戶登入」／
      //    "Sign in with Google"／「Google でログイン」）。寫死 zh_TW 的話，
      //    英文頁上唯一的中文就是最重要的那顆按鈕。
      //    locale 進依賴陣列：切語言時 ref callback 換身分 → React 重新掛載
      //    → 按鈕真的用新語言重畫。不進去的話 URL 換了、按鈕不換。
      window.google.accounts.id.renderButton(node, {
        theme: 'outline', size: 'large', shape: 'pill', text: 'signin_with', locale,
      })
      // 畫沒畫出來，看容器裡有沒有東西 —— 不看 GIS 有沒有回呼（它不會回呼）
      const timer = setTimeout(() => setRendered(node.childElementCount > 0), 4000)
      return () => clearTimeout(timer)
    }
  }, [gisReady, clientId, locale])
  // loadingLabel 可選：/start 是三語的，這句留在中文會是英文頁上唯一的中文
  //（實測抓到）。預設值不變 → 其他呼叫端行為零改動。
  if (import.meta.env.VITE_MOCK === '1') {
    return (
      <button type="button" onClick={mockSignIn} data-testid="mock-signin"
        className="px-5 py-2.5 rounded-full border border-dashed border-amber-400 bg-amber-50 text-amber-800 text-sm font-medium">
        以測試帳號登入（mock 模式・沒有真的 Google 登入）
      </button>
    )
  }
  if (!clientId) return <p className="text-sm text-slate-400">{loadingLabel}</p>
  return (
    <div className="flex flex-col items-center gap-2">
      <div ref={ref} />
      {rendered === false && (
        <p className="text-xs text-amber-700 bg-amber-50 border border-amber-200 rounded-lg px-3 py-2 max-w-xs text-left"
          role="alert" data-testid="gis-fallback">{fallbackLabel}</p>
      )}
      <a href={chooserHref} className="text-xs text-slate-500 underline hover:text-slate-700"
        data-testid="switch-account">{switchLabel}</a>
    </div>
  )
}

// 路由守門：未登入 → 登入畫面（admin 權限由後端 allowlist 判，前端只擋「未登入」）
export function RequireAuth({ children, title = 'QuanBrand 客戶平台' }) {
  const { user, signOut } = useAuth()
  if (!user) {
    return (
      <div className="min-h-dvh flex items-center justify-center bg-slate-50">
        <div className="bg-white rounded-2xl shadow-lg border border-slate-100 p-10 text-center max-w-sm w-full mx-4">
          <img src="/quanturing_logo_light.svg" alt="QuanTuring" className="h-10 mx-auto mb-6" />
          <h1 className="text-xl font-bold text-slate-800 mb-2">{title}</h1>
          <p className="text-sm text-slate-500 mb-8">請使用 Google 帳號登入以繼續</p>
          <div className="flex justify-center"><GoogleButton /></div>
          <p className="text-[11px] text-slate-300 mt-8">© 2026 QuanTuring Inc.</p>
        </div>
      </div>
    )
  }
  return (
    <>
      <div className="fixed top-2 right-3 z-[60] flex items-center gap-2 bg-white/90 backdrop-blur rounded-full border border-slate-200 shadow-sm pl-3 pr-1 py-1">
        <span className="text-xs text-slate-500 max-w-[160px] truncate">{user.email}</span>
        <button onClick={signOut}
          className="text-xs px-2.5 py-1 rounded-full bg-slate-100 hover:bg-slate-200 text-slate-600">
          登出
        </button>
      </div>
      {children}
    </>
  )
}
